import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';

/**
 * Custom hook for fetching AMP schedules data
 */
export const useAMPSchedules = () => {
  const [ampSchedules, setAMPSchedules] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const fetchAMPSchedules = useCallback(async (filters = {}, signal = null) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.aircraftId && filters.aircraftId !== 'ALL') {
        params.aircraftId = filters.aircraftId;
      }
      if (filters.maintenanceType && filters.maintenanceType !== 'ALL') {
        params.maintenanceType = filters.maintenanceType;
      }
      if (filters.recurrenceType && filters.recurrenceType !== 'ALL') {
        params.recurrenceType = filters.recurrenceType;
      }

      const config = signal ? { params, signal } : { params };
      const response = await axios.get('/api/amp-schedules', config);

      if (signal?.aborted) return;

      setAMPSchedules(response.data);
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching AMP schedules:', error);
      showToast('Failed to fetch AMP schedules', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchAircraft = async () => {
    try {
      const response = await axios.get('/api/aircraft');
      setAircraft(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchAssemblies = useCallback(async (aircraftId) => {
    if (!aircraftId || aircraftId === 'ALL') {
      setAssemblies([]);
      return;
    }
    try {
      const params = { isActive: 'true', aircraftId };
      const response = await axios.get('/api/assemblies', { params });
      const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      list.sort((a, b) => {
        const la = `${a.typeName || ''} ${a.serialNumber || ''}`.toLowerCase();
        const lb = `${b.typeName || ''} ${b.serialNumber || ''}`.toLowerCase();
        return la.localeCompare(lb);
      });
      setAssemblies(list);
    } catch (error) {
      console.error('Error fetching assemblies:', error);
      setAssemblies([]);
    }
  }, []);

  useEffect(() => {
    fetchAircraft();
  }, []);

  return {
    ampSchedules,
    aircraft,
    assemblies,
    loading,
    fetchAMPSchedules,
    fetchAircraft,
    fetchAssemblies,
    setAMPSchedules
  };
};
